import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const categoryTitles = {
  family: 'Family Event',
  birthday: 'Birthday / Anniversary',
  community: 'Community Event',
  appointment: 'Appointment',
  reservation: 'Reservation',
  membership: 'Membership'
};

/**
 * AddDateDialog - Dialog for adding date entries
 * 
 * Props:
 * - open: boolean - Whether dialog is open
 * - onOpenChange: function - Toggle dialog open state
 * - category: string - The category of date (family, birthday, etc.)
 * - onSuccess: function(dateData) - Callback with the date data to add
 *                                   Parent component handles actual API call or demo mode
 */
const AddDateDialog = ({ open, onOpenChange, category, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Common fields
    title: '',
    date: '',
    time: '',
    end_date: '',
    location: '',
    notes: '',
    repeat_frequency: 'none',
    
    // Family
    agreement_what: '',
    agreement_status: 'planned',
    linked_family_members: '',
    
    // Birthday
    person_names: '',
    relationship: '',
    gift_reminder_days: 7,
    gift_ideas: '',
    gift_what_they_want: '',
    gift_where_to_buy: '',
    size_clothing: '',
    size_shoe: '',
    preferences: '',
    offered_to_provide: '',
    
    // Community
    organization: '',
    
    // Appointment
    provider_office: '',
    duration_minutes: '',
    would_go_back: '',
    experience_notes: '',
    
    // Reservation
    reservation_type: 'other',
    confirmation_number: '',
    provider_company: '',
    contact_phone: '',
    party_size: '',
    cost: '',
    
    // Membership
    membership_category: 'other',
    membership_status: 'active',
    trial_end_date: '',
    renewal_date: '',
    billing_cost: '',
    billing_frequency: 'month',
    username_email: '',
    password_masked: '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      date: '',
      time: '',
      end_date: '',
      location: '',
      notes: '',
      repeat_frequency: 'none',
      agreement_what: '',
      agreement_status: 'planned',
      linked_family_members: '',
      person_names: '',
      relationship: '',
      gift_reminder_days: 7,
      gift_ideas: '',
      gift_what_they_want: '',
      gift_where_to_buy: '',
      size_clothing: '',
      size_shoe: '',
      preferences: '',
      offered_to_provide: '',
      organization: '',
      provider_office: '',
      duration_minutes: '',
      would_go_back: '',
      experience_notes: '',
      reservation_type: 'other',
      confirmation_number: '',
      provider_company: '',
      contact_phone: '',
      party_size: '',
      cost: '',
      membership_category: 'other',
      membership_status: 'active',
      trial_end_date: '',
      renewal_date: '',
      billing_cost: '',
      billing_frequency: 'month',
      username_email: '',
      password_masked: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.date) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setLoading(true);
    try {
      const dataToSend = {
        category_tab: category,
        title: formData.title,
        date: formData.date,
        time: formData.time || null,
        end_date: formData.end_date || null,
        location: formData.location || null,
        notes: formData.notes || null,
        repeat_frequency: formData.repeat_frequency,
        reminders: [1],
      };
      
      // Add category-specific fields
      if (category === 'family') {
        dataToSend.agreement_what = formData.agreement_what || null;
        dataToSend.agreement_status = formData.agreement_what ? formData.agreement_status : null;
        dataToSend.linked_family_members = formData.linked_family_members 
          ? formData.linked_family_members.split(',').map(s => s.trim()) 
          : [];
      }
      
      if (category === 'birthday') {
        dataToSend.person_names = formData.person_names 
          ? formData.person_names.split(',').map(s => s.trim()) 
          : [];
        dataToSend.relationship = formData.relationship || null;
        dataToSend.gift_reminder_days = parseInt(formData.gift_reminder_days) || 7;
        dataToSend.gift_ideas = formData.gift_ideas || null;
        dataToSend.gift_what_they_want = formData.gift_what_they_want || null;
        dataToSend.gift_where_to_buy = formData.gift_where_to_buy || null;
        dataToSend.size_clothing = formData.size_clothing || null;
        dataToSend.size_shoe = formData.size_shoe || null;
        dataToSend.preferences = formData.preferences || null;
        dataToSend.offered_to_provide = formData.offered_to_provide || null;
        dataToSend.repeat_frequency = 'yearly';
      }
      
      if (category === 'community') {
        dataToSend.organization = formData.organization || null;
        dataToSend.offered_to_provide = formData.offered_to_provide || null;
      }
      
      if (category === 'appointment') {
        dataToSend.provider_office = formData.provider_office || null;
        dataToSend.duration_minutes = formData.duration_minutes ? parseInt(formData.duration_minutes) : null;
        dataToSend.would_go_back = formData.would_go_back || null;
        dataToSend.experience_notes = formData.experience_notes || null;
      }
      
      if (category === 'reservation') {
        dataToSend.reservation_type = formData.reservation_type;
        dataToSend.confirmation_number = formData.confirmation_number || null;
        dataToSend.provider_company = formData.provider_company || null;
        dataToSend.contact_phone = formData.contact_phone || null;
        dataToSend.party_size = formData.party_size ? parseInt(formData.party_size) : null;
        dataToSend.cost = formData.cost ? parseFloat(formData.cost) : null;
      }
      
      if (category === 'membership') {
        dataToSend.membership_category = formData.membership_category;
        dataToSend.membership_status = formData.membership_status;
        dataToSend.trial_end_date = formData.trial_end_date || null;
        dataToSend.renewal_date = formData.renewal_date || null;
        dataToSend.billing_cost = formData.billing_cost ? parseFloat(formData.billing_cost) : null;
        dataToSend.billing_frequency = formData.billing_frequency || null;
        dataToSend.username_email = formData.username_email || null;
        dataToSend.password_masked = formData.password_masked || null;
      }
      
      // Pass data to parent - parent handles API call or demo mode
      await onSuccess(dataToSend);
      resetForm();
    } catch (error) {
      console.error('Failed to create:', error);
      toast.error('Failed to add entry');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add {categoryTitles[category]}</DialogTitle>
          <DialogDescription>
            Fill in the details for your {category} entry
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Common Fields */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder={category === 'birthday' ? "John's Birthday" : "Event title"}
                data-testid="input-date-title"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  data-testid="input-date-date"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="time">Time</Label>
                <Input
                  id="time"
                  type="time"
                  value={formData.time}
                  onChange={(e) => handleChange('time', e.target.value)}
                />
              </div>
            </div>
            
            {(category === 'reservation') && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="end_date">End Date</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => handleChange('end_date', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    placeholder="Address or place name"
                  />
                </div>
              </div>
            )}
            
            {(category !== 'reservation') && (
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  placeholder="Address or place name"
                />
              </div>
            )}
          </div>
          
          {/* Family-specific fields */}
          {category === 'family' && (
            <div className="border-t pt-4 space-y-4">
              <h4 className="text-sm font-medium text-gray-700">Agreement / Commitment</h4>
              <div className="space-y-2">
                <Label htmlFor="agreement_what">What did you agree to?</Label>
                <Input
                  id="agreement_what"
                  value={formData.agreement_what}
                  onChange={(e) => handleChange('agreement_what', e.target.value)}
                  placeholder="e.g., Babysit, Make dinner, Pick up kids"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="agreement_status">Status</Label>
                  <Select value={formData.agreement_status} onValueChange={(v) => handleChange('agreement_status', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="planned">Planned</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="linked_family_members">Family Members</Label>
                  <Input
                    id="linked_family_members"
                    value={formData.linked_family_members}
                    onChange={(e) => handleChange('linked_family_members', e.target.value)}
                    placeholder="Comma separated names"
                  />
                </div>
              </div>
            </div>
          )}
          
          {/* Birthday-specific fields */}
          {category === 'birthday' && (
            <div className="border-t pt-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="person_names">Person Name(s)</Label>
                  <Input
                    id="person_names"
                    value={formData.person_names}
                    onChange={(e) => handleChange('person_names', e.target.value)}
                    placeholder="John, Jane"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="relationship">Relationship</Label>
                  <Input
                    id="relationship"
                    value={formData.relationship}
                    onChange={(e) => handleChange('relationship', e.target.value)}
                    placeholder="e.g., Spouse, Parent, Friend"
                  />
                </div>
              </div>
              
              <h4 className="text-sm font-medium text-gray-700">Gift Planning</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="gift_what_they_want">What they want</Label>
                  <Input
                    id="gift_what_they_want"
                    value={formData.gift_what_they_want}
                    onChange={(e) => handleChange('gift_what_they_want', e.target.value)}
                    placeholder="Their wishlist item"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gift_where_to_buy">Where to buy</Label>
                  <Input
                    id="gift_where_to_buy"
                    value={formData.gift_where_to_buy}
                    onChange={(e) => handleChange('gift_where_to_buy', e.target.value)}
                    placeholder="Store or website"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="gift_ideas">Gift ideas (notes)</Label>
                <Textarea
                  id="gift_ideas"
                  value={formData.gift_ideas}
                  onChange={(e) => handleChange('gift_ideas', e.target.value)}
                  placeholder="Ideas for gifts..."
                  rows={2}
                />
              </div>
              
              <h4 className="text-sm font-medium text-gray-700">Sizes</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="size_clothing">Clothing</Label>
                  <Input
                    id="size_clothing"
                    value={formData.size_clothing}
                    onChange={(e) => handleChange('size_clothing', e.target.value)}
                    placeholder="M, L, XL..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="size_shoe">Shoe</Label>
                  <Input
                    id="size_shoe"
                    value={formData.size_shoe}
                    onChange={(e) => handleChange('size_shoe', e.target.value)}
                    placeholder="9, 10..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gift_reminder_days">Gift reminder</Label>
                  <Select value={String(formData.gift_reminder_days)} onValueChange={(v) => handleChange('gift_reminder_days', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 days before</SelectItem>
                      <SelectItem value="7">7 days before</SelectItem>
                      <SelectItem value="14">14 days before</SelectItem>
                      <SelectItem value="30">30 days before</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="preferences">Preferences (colors, dislikes)</Label>
                <Input
                  id="preferences"
                  value={formData.preferences}
                  onChange={(e) => handleChange('preferences', e.target.value)}
                  placeholder="Favorite colors, allergies, dislikes..."
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="offered_to_provide">You offered to provide</Label>
                <Input
                  id="offered_to_provide"
                  value={formData.offered_to_provide}
                  onChange={(e) => handleChange('offered_to_provide', e.target.value)}
                  placeholder="e.g., Bring dessert, Host dinner"
                />
              </div>
            </div>
          )}
          
          {/* Community-specific fields */}
          {category === 'community' && (
            <div className="border-t pt-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="organization">Organization / Group</Label>
                  <Input
                    id="organization"
                    value={formData.organization}
                    onChange={(e) => handleChange('organization', e.target.value)}
                    placeholder="Church, Club, etc."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="repeat_frequency">Repeats</Label>
                  <Select value={formData.repeat_frequency} onValueChange={(v) => handleChange('repeat_frequency', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Does not repeat</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="offered_to_provide">You offered to provide</Label>
                <Input
                  id="offered_to_provide"
                  value={formData.offered_to_provide}
                  onChange={(e) => handleChange('offered_to_provide', e.target.value)}
                  placeholder="e.g., Bring salad, Teach lesson, Set up chairs"
                />
              </div>
            </div>
          )}
          
          {/* Appointment-specific fields */}
          {category === 'appointment' && (
            <div className="border-t pt-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="provider_office">Provider / Office</Label>
                  <Input
                    id="provider_office"
                    value={formData.provider_office}
                    onChange={(e) => handleChange('provider_office', e.target.value)}
                    placeholder="Dr. Smith, ABC Clinic"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="duration_minutes">Duration (minutes)</Label>
                  <Input
                    id="duration_minutes"
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => handleChange('duration_minutes', e.target.value)}
                    placeholder="30, 60..."
                  />
                </div>
              </div>
              
              <h4 className="text-sm font-medium text-gray-700">Experience (after visit)</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="would_go_back">Would you go back?</Label>
                  <Select value={formData.would_go_back} onValueChange={(v) => handleChange('would_go_back', v)}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="maybe">Maybe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="experience_notes">Experience notes</Label>
                <Textarea
                  id="experience_notes"
                  value={formData.experience_notes}
                  onChange={(e) => handleChange('experience_notes', e.target.value)}
                  placeholder="How was the experience?"
                  rows={2}
                />
              </div>
            </div>
          )}
          
          {/* Reservation-specific fields */}
          {category === 'reservation' && (
            <div className="border-t pt-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="reservation_type">Type</Label>
                  <Select value={formData.reservation_type} onValueChange={(v) => handleChange('reservation_type', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hotel">Hotel</SelectItem>
                      <SelectItem value="restaurant">Restaurant</SelectItem>
                      <SelectItem value="flight">Flight</SelectItem>
                      <SelectItem value="rental">Rental Car</SelectItem>
                      <SelectItem value="event">Event</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmation_number">Confirmation #</Label>
                  <Input
                    id="confirmation_number"
                    value={formData.confirmation_number}
                    onChange={(e) => handleChange('confirmation_number', e.target.value)}
                    placeholder="ABC123XYZ"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="provider_company">Provider / Company</Label>
                  <Input
                    id="provider_company"
                    value={formData.provider_company}
                    onChange={(e) => handleChange('provider_company', e.target.value)}
                    placeholder="Hilton, OpenTable..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_phone">Contact Phone</Label>
                  <Input
                    id="contact_phone"
                    value={formData.contact_phone}
                    onChange={(e) => handleChange('contact_phone', e.target.value)}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="party_size">Party Size</Label>
                  <Input
                    id="party_size"
                    type="number"
                    value={formData.party_size}
                    onChange={(e) => handleChange('party_size', e.target.value)}
                    placeholder="2"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cost">Cost ($)</Label>
                  <Input
                    id="cost"
                    type="number"
                    step="0.01"
                    value={formData.cost}
                    onChange={(e) => handleChange('cost', e.target.value)}
                    placeholder="150.00"
                  />
                </div>
              </div>
            </div>
          )}
          
          {/* Membership-specific fields */}
          {category === 'membership' && (
            <div className="border-t pt-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="membership_category">Category</Label>
                  <Select value={formData.membership_category} onValueChange={(v) => handleChange('membership_category', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="streaming">Streaming</SelectItem>
                      <SelectItem value="fitness">Fitness</SelectItem>
                      <SelectItem value="software">Software</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="membership_status">Status</Label>
                  <Select value={formData.membership_status} onValueChange={(v) => handleChange('membership_status', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="trial">Trial</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="billing_cost">Cost ($)</Label>
                  <Input
                    id="billing_cost"
                    type="number"
                    step="0.01"
                    value={formData.billing_cost}
                    onChange={(e) => handleChange('billing_cost', e.target.value)}
                    placeholder="9.99"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="billing_frequency">Billing Frequency</Label>
                  <Select value={formData.billing_frequency} onValueChange={(v) => handleChange('billing_frequency', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="week">Weekly</SelectItem>
                      <SelectItem value="month">Monthly</SelectItem>
                      <SelectItem value="year">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="trial_end_date">Trial End Date</Label>
                  <Input
                    id="trial_end_date"
                    type="date"
                    value={formData.trial_end_date}
                    onChange={(e) => handleChange('trial_end_date', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="renewal_date">Renewal Date</Label>
                  <Input
                    id="renewal_date"
                    type="date"
                    value={formData.renewal_date}
                    onChange={(e) => handleChange('renewal_date', e.target.value)}
                  />
                </div>
              </div>
              
              <h4 className="text-sm font-medium text-gray-700">Login Credentials (optional)</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="username_email">Username / Email</Label>
                  <Input
                    id="username_email"
                    value={formData.username_email}
                    onChange={(e) => handleChange('username_email', e.target.value)}
                    placeholder="user@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password_masked">Password</Label>
                  <Input
                    id="password_masked"
                    type="password"
                    value={formData.password_masked}
                    onChange={(e) => handleChange('password_masked', e.target.value)}
                    placeholder="••••••••"
                  />
                </div>
              </div>
            </div>
          )}
          
          {/* Notes - Common */}
          <div className="border-t pt-4 space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Additional notes..."
              rows={2}
            />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-[#C5D9ED] hover:bg-[#B0CCE3] text-gray-900"
              data-testid="submit-date-btn"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Entry
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddDateDialog;
